"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { onTerminalMessage, sendWsMessage } from "../lib/websocket";
import { useWsStatus } from "../store/selectors";
import "@xterm/xterm/css/xterm.css";

// xterm.js types for dynamic import
type XTermTerminal = import("@xterm/xterm").Terminal;
type XTermFitAddon = import("@xterm/addon-fit").FitAddon;
type XTermWebLinksAddon = import("@xterm/addon-web-links").WebLinksAddon;

interface TerminalViewProps {
  channelId: string;
  /** Called when the user clicks "Back to chat" */
  onClose?: () => void;
}

type ConnectionState = "connecting" | "connected" | "disconnected" | "error";

// ─── Touch Toolbar ───────────────────────────────────────────────────────────

interface ToolbarProps {
  channelId: string;
  state: ConnectionState;
  terminalRef: React.RefObject<XTermTerminal | null>;
}

/** Modifier key state — Ctrl and Alt are sticky (tap to toggle, auto-clear after next key) */
function TouchToolbar({ channelId, state, terminalRef }: ToolbarProps) {
  const [ctrlActive, setCtrlActive] = useState(false);
  const [altActive, setAltActive] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const pasteInputRef = useRef<HTMLTextAreaElement>(null);

  const send = useCallback((data: string) => {
    if (state !== "connected") return;
    sendWsMessage({ type: "terminal.input", channelId, data });
  }, [channelId, state]);

  /** Send a key, applying Ctrl/Alt modifiers then clearing them */
  const sendKey = useCallback((key: string, ctrl = false, alt = false) => {
    const applyCtrl = ctrl || ctrlActive;
    const applyAlt = alt || altActive;

    let data = key;
    if (applyCtrl && key.length === 1 && key >= "a" && key <= "z") {
      data = String.fromCharCode(key.charCodeAt(0) - 96);
    } else if (applyCtrl && key === "c") {
      data = "\x03";
    }
    if (applyAlt) {
      data = "\x1b" + data;
    }

    send(data);
    setCtrlActive(false);
    setAltActive(false);
  }, [send, ctrlActive, altActive]);

  /** Open a native textarea so the user can long-press → Paste into it */
  const handlePasteClick = useCallback(() => {
    setPasteMode(true);
    // Focus the textarea on next frame so the native paste menu can appear
    requestAnimationFrame(() => pasteInputRef.current?.focus());
  }, []);

  /** When the user pastes or types into the paste textarea, forward it and close */
  const handlePasteInput = useCallback(() => {
    const val = pasteInputRef.current?.value;
    if (val) {
      send(val);
      pasteInputRef.current!.value = "";
    }
    setPasteMode(false);
    terminalRef.current?.focus();
  }, [send, terminalRef]);

  /**
   * Prevent all toolbar buttons from stealing focus away from xterm's
   * hidden textarea. Without this, tapping any button dismisses the
   * mobile virtual keyboard.
   */
  const preventFocusSteal = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
  }, []);

  return (
    <div style={toolbarStyle}>
      {pasteMode ? (
        /* Native paste field: user can long-press → Paste, then tap Send */
        <div style={pasteRowStyle}>
          <textarea
            ref={pasteInputRef}
            rows={1}
            placeholder="Long-press to paste, then tap Send →"
            style={pasteTextareaStyle}
            onPaste={() => {
              // Small delay to let the paste value populate
              setTimeout(handlePasteInput, 50);
            }}
          />
          <button
            onMouseDown={preventFocusSteal}
            onClick={handlePasteInput}
            style={toolbarBtnStyle(true)}
          >
            Send
          </button>
          <button
            onMouseDown={preventFocusSteal}
            onClick={() => { setPasteMode(false); terminalRef.current?.focus(); }}
            style={toolbarBtnStyle(false)}
          >
            ✕
          </button>
        </div>
      ) : (
        /* Normal toolbar */
        <>
          <button
            onMouseDown={preventFocusSteal}
            onClick={() => setCtrlActive((v) => !v)}
            style={toolbarBtnStyle(ctrlActive)}
          >
            Ctrl
          </button>
          <button
            onMouseDown={preventFocusSteal}
            onClick={() => setAltActive((v) => !v)}
            style={toolbarBtnStyle(altActive)}
          >
            Alt
          </button>
          <div style={toolbarDivider} />
          <button onMouseDown={preventFocusSteal} onClick={() => sendKey("\t")} style={toolbarBtnStyle(false)}>
            Tab
          </button>
          <button onMouseDown={preventFocusSteal} onClick={() => send("\x1b")} style={toolbarBtnStyle(false)}>
            Esc
          </button>
          <div style={toolbarDivider} />
          <button onMouseDown={preventFocusSteal} onClick={() => send("\x1b[A")} style={toolbarBtnStyle(false)}>
            ▲
          </button>
          <button onMouseDown={preventFocusSteal} onClick={() => send("\x1b[B")} style={toolbarBtnStyle(false)}>
            ▼
          </button>
          <button onMouseDown={preventFocusSteal} onClick={() => send("\x1b[D")} style={toolbarBtnStyle(false)}>
            ◀
          </button>
          <button onMouseDown={preventFocusSteal} onClick={() => send("\x1b[C")} style={toolbarBtnStyle(false)}>
            ▶
          </button>
          <div style={toolbarDivider} />
          <button onMouseDown={preventFocusSteal} onClick={handlePasteClick} style={toolbarBtnStyle(false)}>
            📋
          </button>
        </>
      )}
    </div>
  );
}

export function TerminalView({ channelId, onClose }: TerminalViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<XTermTerminal | null>(null);
  const fitAddonRef = useRef<XTermFitAddon | null>(null);
  const [state, setState] = useState<ConnectionState>("connecting");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const wsStatus = useWsStatus();

  const handleReconnect = useCallback(() => {
    setState("connecting");
    setErrorMsg(null);
    terminalRef.current?.clear();
    sendWsMessage({ type: "terminal.open", channelId });
  }, [channelId]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (wsStatus !== "authenticated") return;

    let terminal: XTermTerminal;
    let fitAddon: XTermFitAddon;
    let disposed = false;

    // Dynamic import to avoid SSR issues with xterm.js
    async function init() {
      const [
        { Terminal },
        { FitAddon },
        { WebLinksAddon },
      ] = await Promise.all([
        import("@xterm/xterm"),
        import("@xterm/addon-fit"),
        import("@xterm/addon-web-links"),
      ]);

      if (disposed) return;

      terminal = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', Menlo, Monaco, 'Courier New', monospace",
        theme: {
          background: "#0a0a0a",
          foreground: "#e0e0e0",
          cursor: "#e0e0e0",
          selectionBackground: "rgba(255, 255, 255, 0.2)",
          black: "#0a0a0a",
          red: "#ff5555",
          green: "#50fa7b",
          yellow: "#f1fa8c",
          blue: "#6272a4",
          magenta: "#ff79c6",
          cyan: "#8be9fd",
          white: "#e0e0e0",
          brightBlack: "#555555",
          brightRed: "#ff6e6e",
          brightGreen: "#69ff94",
          brightYellow: "#ffffa5",
          brightBlue: "#d6acff",
          brightMagenta: "#ff92df",
          brightCyan: "#a4ffff",
          brightWhite: "#ffffff",
        },
        scrollback: 5000,
        convertEol: true,
      });

      fitAddon = new FitAddon();
      terminal.loadAddon(fitAddon);
      terminal.loadAddon(new WebLinksAddon());

      terminalRef.current = terminal;
      fitAddonRef.current = fitAddon;

      terminal.open(containerRef.current!);

      // Small delay for the container to have its final size
      requestAnimationFrame(() => {
        if (disposed) return;
        fitAddon.fit();
      });

      // Send keystrokes to the server
      terminal.onData((data) => {
        if (stateRef.current === "connected") {
          sendWsMessage({ type: "terminal.input", channelId, data });
        }
      });

      // Handle resize
      const resizeObserver = new ResizeObserver(() => {
        if (disposed) return;
        try {
          fitAddon.fit();
        } catch {
          // ignore if terminal not ready
        }
      });
      resizeObserver.observe(containerRef.current!);

      terminal.onResize(({ cols, rows }) => {
        if (stateRef.current === "connected") {
          sendWsMessage({ type: "terminal.resize", channelId, cols, rows });
        }
      });

      // Subscribe to terminal messages from WS
      const unsubscribe = onTerminalMessage((msg) => {
        if (msg.channelId !== channelId) return;

        switch (msg.type) {
          case "terminal.opened":
            setState("connected");
            terminal.focus();
            break;

          case "terminal.output":
            terminal.write(msg.data as string);
            break;

          case "terminal.closed": {
            setState("disconnected");
            const reason = msg.reason as string;
            const label =
              reason === "idle_timeout" ? "Session closed (idle timeout)" :
              reason === "agent_disconnected" ? "Agent disconnected" :
              reason === "user_closed" ? "Session closed" :
              "Session closed";
            terminal.write(`\r\n\x1b[33m${label}\x1b[0m\r\n`);
            break;
          }

          case "terminal.error":
            setState("error");
            setErrorMsg(msg.message as string);
            terminal.write(`\r\n\x1b[31mError: ${msg.message}\x1b[0m\r\n`);
            break;
        }
      });

      // Request terminal open
      terminal.write("\x1b[90mConnecting to terminal...\x1b[0m\r\n");
      sendWsMessage({ type: "terminal.open", channelId });

      // Cleanup
      return () => {
        disposed = true;
        unsubscribe();
        resizeObserver.disconnect();

        // Send close if connected
        if (stateRef.current === "connected") {
          sendWsMessage({ type: "terminal.close", channelId });
        }

        terminal.dispose();
        terminalRef.current = null;
        fitAddonRef.current = null;
      };
    }

    const cleanupPromise = init();

    return () => {
      disposed = true;
      cleanupPromise.then((cleanup) => cleanup?.());
    };
  }, [channelId, wsStatus]);

  return (
    <div style={wrapperStyle}>
      <div style={headerStyle}>
        <div style={headerLeftStyle}>
          <span style={dotStyle(state)} />
          <span style={{ color: "#e0e0e0", fontSize: "0.85rem", fontWeight: 500 }}>
            Terminal
          </span>
          <span style={{ color: "#666", fontSize: "0.75rem" }}>
            {state === "connecting" ? "Connecting..." :
             state === "connected" ? "Connected" :
             state === "error" ? "Error" :
             "Disconnected"}
          </span>
        </div>
        <div style={headerRightStyle}>
          {(state === "disconnected" || state === "error") && (
            <button onClick={handleReconnect} style={reconnectBtnStyle}>
              Reconnect
            </button>
          )}
          {onClose && (
            <button onClick={onClose} style={closeBtnStyle}>
              ← Chat
            </button>
          )}
        </div>
      </div>
      <div ref={containerRef} style={terminalContainerStyle} />
      <TouchToolbar channelId={channelId} state={state} terminalRef={terminalRef} />
      {errorMsg && state === "error" && (
        <div style={errorBannerStyle}>{errorMsg}</div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const wrapperStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  background: "#0a0a0a",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 16px",
  background: "#111",
  borderBottom: "1px solid #222",
  flexShrink: 0,
};

const headerLeftStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const headerRightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

function dotStyle(state: ConnectionState): React.CSSProperties {
  const color =
    state === "connected" ? "#50fa7b" :
    state === "connecting" ? "#f1fa8c" :
    state === "error" ? "#ff5555" :
    "#666";
  return {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: color,
    flexShrink: 0,
  };
}

const terminalContainerStyle: React.CSSProperties = {
  flex: 1,
  padding: "4px",
  overflow: "hidden",
};

const reconnectBtnStyle: React.CSSProperties = {
  padding: "4px 12px",
  fontSize: "0.75rem",
  background: "#222",
  color: "#e0e0e0",
  border: "1px solid #444",
  borderRadius: "4px",
  cursor: "pointer",
};

const closeBtnStyle: React.CSSProperties = {
  padding: "4px 12px",
  fontSize: "0.75rem",
  background: "none",
  color: "#888",
  border: "1px solid #444",
  borderRadius: "4px",
  cursor: "pointer",
};

const errorBannerStyle: React.CSSProperties = {
  padding: "8px 16px",
  background: "rgba(255, 85, 85, 0.15)",
  color: "#ff5555",
  fontSize: "0.8rem",
  borderTop: "1px solid rgba(255, 85, 85, 0.3)",
  flexShrink: 0,
};

// ─── Toolbar Styles ──────────────────────────────────────────────────────────

const toolbarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  background: "#161616",
  borderTop: "1px solid #222",
  flexShrink: 0,
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
};

function toolbarBtnStyle(active: boolean): React.CSSProperties {
  return {
    padding: "6px 10px",
    fontSize: "0.75rem",
    fontWeight: 600,
    fontFamily: "'SF Mono', 'Fira Code', Menlo, monospace",
    background: active ? "#444" : "#222",
    color: active ? "#fff" : "#bbb",
    border: active ? "1px solid #666" : "1px solid #333",
    borderRadius: 4,
    cursor: "pointer",
    flexShrink: 0,
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    lineHeight: 1,
  };
}

const toolbarDivider: React.CSSProperties = {
  width: 1,
  height: 18,
  background: "#333",
  flexShrink: 0,
};

const pasteRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flex: 1,
};

const pasteTextareaStyle: React.CSSProperties = {
  flex: 1,
  padding: "6px 8px",
  fontSize: "0.8rem",
  fontFamily: "'SF Mono', 'Fira Code', Menlo, monospace",
  background: "#1a1a1a",
  color: "#e0e0e0",
  border: "1px solid #444",
  borderRadius: 4,
  outline: "none",
  resize: "none",
  lineHeight: 1.3,
};
